import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  ListItemText,
  MenuItem,
  Popover,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { AppOutlinedTextField } from '@/components/shared/AppOutlinedTextField';
import InlineFeedback, { type InlineFeedbackProps } from '@/components/shared/InlineFeedback';
import { AI_MEAL_PLAN_MAX_DAYS, MEAL_PLAN_MAX_DAYS } from '@/components/meal-planning/MealPlanScheduleFields';
import type { Client } from '@/types/client';
import type { SxProps, Theme } from '@mui/material/styles';

type MealPlanCategory = 'manual' | 'auto' | 'ai' | 'generic';

export type GenericPlanCalorieTargetForm = {
  mode: 'direct' | 'reference_person';
  dailyCalories: string;
  ageYears: string;
  gender: 'male' | 'female' | '';
  weightKg: string;
  heightCm: string;
  activityLevel: string;
  weightGoal:
    | 'moderate_loss'
    | 'mild_loss'
    | 'maintain'
    | 'mild_gain'
    | 'moderate_gain';
};

/** Shared with meal name, client picker, and day count — one visual system. */
const addMealPlanDialogOutlinedFieldSx: SxProps<Theme> = {
  '& .MuiOutlinedInput-root': {
    height: 44,
    borderRadius: '8px',
    fontFamily: 'Inter, sans-serif',
    fontSize: '1rem',
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Inter, sans-serif',
    color: '#6b7280',
  },
};

/** Multi-select tags can grow past one line. */
const addMealPlanDialogOutlinedSelectFieldSx: SxProps<Theme> = {
  '& .MuiOutlinedInput-root': {
    minHeight: 44,
    borderRadius: '8px',
    fontFamily: 'Inter, sans-serif',
    fontSize: '1rem',
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Inter, sans-serif',
    color: '#6b7280',
  },
};

const addMealPlanOptions: Array<{
  key: MealPlanCategory;
  title: string;
  description: string;
  gradient: string;
}> = [
  {
    key: 'manual',
    title: 'Manual Meal Planning',
    description: 'Create custom meal plans manually by selecting recipes and ingredients for your clients.',
    gradient: 'linear-gradient(132deg, #3E8FD8 0%, #42A0EB 52%, #63C6F4 100%)',
  },
  {
    key: 'ai',
    title: 'AI Generated Meal Plan',
    description: 'Create AI generated meal plans for your clients.',
    gradient: 'linear-gradient(132deg, #2CB6C8 0%, #39C8BE 52%, #57D8CB 100%)',
  },
  {
    key: 'generic',
    title: 'Cohort Meal Plan',
    description: 'Create cohort meal plans by selecting recipes and ingredients for your clients.',
    gradient: 'linear-gradient(132deg, #F0B449 0%, #F8BC4D 52%, #FFD26A 100%)',
  },
];

export interface ClientDetailsV2AddMealPlanDialogProps {
  open: boolean;
  onClose: () => void;
  showClientSelection?: boolean;
  selectedClientQuery?: string;
  onClientSearchChange?: (value: string) => void;
  isClientDropdownOpen?: boolean;
  onClientDropdownOpenChange?: (open: boolean) => void;
  clientOptions?: Client[];
  isClientsLoading?: boolean;
  getClientDisplayName?: (client: Client) => string;
  getClientConditionSummary?: (client: Client) => string;
  onPickClient?: (client: Client) => void;
  newMealName: string;
  onNewMealNameChange: (value: string) => void;
  newMealDays: number;
  onNewMealDaysChange: (value: number) => void;
  genericClinicalTagOptions?: Array<{ value: string; label: string }>;
  selectedGenericClinicalTags?: string[];
  onGenericClinicalTagsChange?: (value: string[]) => void;
  onGenericClinicalTagsOpen?: () => void;
  isGenericClinicalTagsLoading?: boolean;
  genericCalorieTarget?: GenericPlanCalorieTargetForm;
  onGenericCalorieTargetChange?: (value: GenericPlanCalorieTargetForm) => void;
  selectedPlanType: MealPlanCategory;
  onSelectedPlanTypeChange: (value: MealPlanCategory) => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
  title?: string;
  subtitle?: string;
  mealNameLabel?: string;
  showDaysField?: boolean;
  showPlanTypeOptions?: boolean;
  showGenericPlanTypeOption?: boolean;
  showGenericClinicalTags?: boolean;
  submitLabel?: string;
  submittingLabel?: string;
  /** Max dialog width in px (default 760). */
  paperMaxWidth?: number;
  /** Smaller title, labels, inputs, and submit text (e.g. generic meal-planning modal). */
  compactTypography?: boolean;
  fieldErrors?: {
    client?: string;
    mealName?: string;
    days?: string;
    clinicalTags?: string;
    calorieTarget?: string;
    ageYears?: string;
    gender?: string;
    weightKg?: string;
    heightCm?: string;
    activityLevel?: string;
  };
  formFeedback?: Pick<InlineFeedbackProps, 'variant' | 'title' | 'message'> | null;
}

export const ClientDetailsV2AddMealPlanDialog: React.FC<ClientDetailsV2AddMealPlanDialogProps> = ({
  open,
  onClose,
  showClientSelection = true,
  selectedClientQuery = '',
  onClientSearchChange = () => {},
  isClientDropdownOpen = false,
  onClientDropdownOpenChange = () => {},
  clientOptions = [],
  isClientsLoading = false,
  getClientDisplayName = (client) => client.fullName || client.email || 'Unnamed Client',
  getClientConditionSummary = () => '',
  onPickClient = () => {},
  newMealName,
  onNewMealNameChange,
  newMealDays,
  onNewMealDaysChange,
  genericClinicalTagOptions = [],
  selectedGenericClinicalTags = [],
  onGenericClinicalTagsChange,
  onGenericClinicalTagsOpen,
  isGenericClinicalTagsLoading = false,
  genericCalorieTarget,
  onGenericCalorieTargetChange,
  selectedPlanType,
  onSelectedPlanTypeChange,
  onSubmit,
  isSubmitting = false,
  title = 'Add New Meal Plan',
  subtitle = 'Configure a meal plan',
  mealNameLabel = 'Meal Name',
  showDaysField = true,
  showPlanTypeOptions = true,
  showGenericPlanTypeOption = true,
  showGenericClinicalTags = true,
  submitLabel = 'Add Meal Plan',
  submittingLabel = 'Creating...',
  paperMaxWidth = 760,
  compactTypography = false,
  fieldErrors,
  formFeedback,
}) => {
  const clientSearchAnchorRef = React.useRef<HTMLDivElement | null>(null);
  const [clientMenuAnchorEl, setClientMenuAnchorEl] = React.useState<HTMLElement | null>(null);
  const shouldShowDaysField = showDaysField && selectedPlanType !== 'generic';
  const dayCountOptions = React.useMemo(() => {
    const maxDays = selectedPlanType === 'ai' || selectedPlanType === 'auto'
      ? AI_MEAL_PLAN_MAX_DAYS
      : MEAL_PLAN_MAX_DAYS;
    return Array.from({ length: maxDays }, (_, index) => index + 1);
  }, [selectedPlanType]);
  const visibleAddMealPlanOptions = React.useMemo(
    () => addMealPlanOptions.filter((option) => showGenericPlanTypeOption || option.key !== 'generic'),
    [showGenericPlanTypeOption]
  );

  React.useEffect(() => {
    if (!open) {
      setClientMenuAnchorEl(null);
    }
  }, [open]);

  const openClientDropdown = React.useCallback(() => {
    if (clientSearchAnchorRef.current) {
      setClientMenuAnchorEl(clientSearchAnchorRef.current);
    }
    onClientDropdownOpenChange(true);
  }, [onClientDropdownOpenChange]);

  const compactFieldSx = compactTypography
    ? {
        '& .MuiOutlinedInput-root': { fontSize: '0.875rem' },
        '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
      }
    : {};

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        'data-demo-id': 'meal-plan-dialog',
        sx: {
          width: { xs: 'calc(100% - 24px)', sm: `min(92vw, ${paperMaxWidth}px)` },
          maxWidth: paperMaxWidth,
          borderRadius: '16px',
          bgcolor: '#fff',
          m: { xs: 1.5, sm: 2 },
          overflow: 'visible',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
        },
      }}
    >
      <DialogContent
        sx={{
          p: { xs: 2, sm: 2.5, md: 3 },
          pb: { xs: 2.3, sm: 2.6 },
          overflow: 'auto',
          flex: '1 1 auto',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ width: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
            <Box>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: compactTypography
                    ? { xs: '1.2rem', sm: '1.35rem' }
                    : { xs: '1.6rem', sm: '2rem' },
                  fontWeight: 500,
                  color: '#24262B',
                  lineHeight: 1.2,
                }}
              >
                {title}
              </Typography>
              {subtitle?.trim() ? (
                <Typography
                  sx={{
                    mt: 0.6,
                    fontFamily: 'Inter, sans-serif',
                    fontSize: compactTypography
                      ? { xs: '0.875rem', sm: '0.9375rem' }
                      : { xs: '0.96rem', sm: '1.05rem' },
                    color: '#4F5662',
                  }}
                >
                  {subtitle}
                </Typography>
              ) : null}
            </Box>

            <IconButton
              onClick={onClose}
              sx={{
                width: 34,
                height: 34,
                bgcolor: '#E8EDF4',
                color: '#3f4853',
                '&:hover': { bgcolor: '#dee6ef' },
              }}
            >
              <CloseRoundedIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>

          <Stack spacing={compactTypography ? 2.2 : 3} sx={{ mt: compactTypography ? 2.2 : 2.8, width: '100%' }}>
            {showClientSelection ? (
              <Box ref={clientSearchAnchorRef} sx={{ position: 'relative', width: '100%' }}>
                <TextField
                  fullWidth
                  label="Select the Client to Assign Meal plan"
                  value={selectedClientQuery}
                  error={Boolean(fieldErrors?.client)}
                  helperText={fieldErrors?.client}
                  onFocus={openClientDropdown}
                  onClick={openClientDropdown}
                  onChange={(event) => onClientSearchChange(event.target.value)}
                  placeholder="Search Clients..."
                  size="small"
                  variant="outlined"
                  sx={[
                    addMealPlanDialogOutlinedFieldSx,
                    compactFieldSx,
                    {
                      '& .MuiOutlinedInput-root': {
                        bgcolor: '#fff',
                        '& fieldset': { borderColor: '#D8DEE7' },
                      },
                    },
                  ]}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon sx={{ fontSize: 18, color: '#9AA3B2' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <ExpandMoreRoundedIcon sx={{ fontSize: 18, color: '#98A2B3' }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <Popover
                    open={isClientDropdownOpen && Boolean(clientMenuAnchorEl)}
                    anchorEl={clientMenuAnchorEl}
                    onClose={() => {
                      onClientDropdownOpenChange(false);
                      setClientMenuAnchorEl(null);
                    }}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    slotProps={{
                      paper: {
                        sx: {
                          zIndex: 1400,
                          mt: 0.75,
                          border: '1px solid #e4e7ec',
                          borderRadius: '10px',
                          bgcolor: '#fff',
                          boxShadow: '0 10px 30px rgba(16, 24, 40, 0.08)',
                          maxHeight: 370,
                          overflowY: 'auto',
                          minWidth: clientMenuAnchorEl ? clientMenuAnchorEl.offsetWidth : undefined,
                          width: 'max-content',
                          maxWidth: 'min(92vw, 560px)',
                        },
                      },
                    }}
                    disableRestoreFocus
                >
                    {isClientsLoading ? (
                      <Box sx={{ py: 2.2, display: 'grid', placeItems: 'center' }}>
                        <CircularProgress size={18} />
                      </Box>
                    ) : clientOptions.length ? (
                      clientOptions.map((client) => {
                        const displayName = getClientDisplayName(client);
                        const conditionSummary = getClientConditionSummary(client);
                        const emailText = client.email || 'no-email@client.com';
                        const statusText = client.status ? client.status.charAt(0).toUpperCase() + client.status.slice(1) : 'Active';

                        return (
                          <Box
                            key={client.id}
                            onMouseDown={(event) => {
                              event.preventDefault();
                            }}
                            onClick={() => onPickClient(client)}
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: '34px minmax(0, max-content)',
                              gap: 1.1,
                              px: 1.4,
                              py: 1.05,
                              borderBottom: '1px solid #f1f3f7',
                              cursor: 'pointer',
                              '&:hover': { bgcolor: '#f8fbfd' },
                            }}
                          >
                            <Box
                              sx={{
                                width: 34,
                                height: 34,
                                borderRadius: '10px',
                                bgcolor: '#edf3ff',
                                display: 'grid',
                                placeItems: 'center',
                              }}
                            >
                              <PersonRoundedIcon sx={{ fontSize: 18, color: '#4A8DFF' }} />
                            </Box>

                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                sx={{
                                  fontFamily: 'Inter, sans-serif',
                                  fontSize: '1rem',
                                  lineHeight: 1.2,
                                  color: '#334155',
                                  whiteSpace: 'normal',
                                  wordBreak: 'break-word',
                                }}
                              >
                                {displayName}
                              </Typography>
                              {conditionSummary ? (
                                <Typography sx={{ mt: 0.2, fontFamily: 'Inter, sans-serif', fontSize: '0.88rem', color: '#8A93A5' }}>
                                  {conditionSummary}
                                </Typography>
                              ) : null}
                              <Typography
                                sx={{
                                  mt: 0.2,
                                  fontFamily: 'Inter, sans-serif',
                                  fontSize: '0.82rem',
                                  fontWeight: 600,
                                  color: '#B0B8C5',
                                  whiteSpace: 'normal',
                                  wordBreak: 'break-word',
                                }}
                              >
                                {emailText} <Box component="span" sx={{ px: 0.5 }}>•</Box> {statusText}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })
                    ) : (
                      <Box sx={{ p: 1.6 }}>
                        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.85rem', color: '#98A2B3' }}>
                          No clients found
                        </Typography>
                      </Box>
                    )}
                </Popover>
              </Box>
            ) : null}

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: shouldShowDaysField ? { xs: '1fr', sm: '1fr 1fr' } : '1fr',
                gap: 1.2,
                width: '100%',
              }}
            >
              <AppOutlinedTextField
                label={mealNameLabel}
                placeholder="Enter meal name"
                value={newMealName}
                onChange={(event) => onNewMealNameChange(event.target.value)}
                error={Boolean(fieldErrors?.mealName)}
                helperText={fieldErrors?.mealName}
                size="small"
                sx={[addMealPlanDialogOutlinedFieldSx, compactFieldSx]}
              />

              {shouldShowDaysField ? (
                <AppOutlinedTextField
                  label="No of Days"
                  select
                  value={String(newMealDays)}
                  onChange={(event) => onNewMealDaysChange(Number(event.target.value))}
                  error={Boolean(fieldErrors?.days)}
                  helperText={fieldErrors?.days}
                  size="small"
                  sx={[addMealPlanDialogOutlinedFieldSx, compactFieldSx]}
                >
                  {dayCountOptions.map((day) => (
                    <MenuItem key={day} value={String(day)}>
                      {day}
                    </MenuItem>
                  ))}
                </AppOutlinedTextField>
              ) : null}
            </Box>

            {showGenericClinicalTags && selectedPlanType === 'generic' ? (
              <AppOutlinedTextField
                fullWidth
                label="Clinical Tags"
                select
                value={selectedGenericClinicalTags}
                onChange={(event) => {
                  const value = event.target.value;
                  onGenericClinicalTagsChange?.(Array.isArray(value) ? value : String(value).split(','));
                }}
                error={Boolean(fieldErrors?.clinicalTags)}
                helperText={fieldErrors?.clinicalTags}
                size="small"
                disabled={isGenericClinicalTagsLoading}
                onFocus={onGenericClinicalTagsOpen}
                SelectProps={{
                  multiple: true,
                  MenuProps: {
                    disablePortal: false,
                    keepMounted: true,
                    marginThreshold: null,
                    variant: 'menu',
                    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                    transformOrigin: { vertical: 'top', horizontal: 'left' },
                    PaperProps: {
                      sx: {
                        mt: 0.75,
                        maxHeight: 300,
                        borderRadius: '10px',
                        border: '1px solid #e4e7ec',
                        boxShadow: '0 10px 30px rgba(16, 24, 40, 0.08)',
                      },
                    },
                  },
                  onOpen: onGenericClinicalTagsOpen,
                  renderValue: (selected) => {
                    const selectedValues = Array.isArray(selected) ? selected : [];
                    return selectedValues
                      .map((value) => genericClinicalTagOptions.find((tag) => tag.value === value)?.label ?? value)
                      .join(', ');
                  },
                }}
                sx={[addMealPlanDialogOutlinedSelectFieldSx, compactFieldSx]}
              >
                {genericClinicalTagOptions.map((tag) => (
                  <MenuItem key={tag.value} value={tag.value}>
                    <Checkbox checked={selectedGenericClinicalTags.includes(tag.value)} size="small" />
                    <ListItemText primary={tag.label} />
                  </MenuItem>
                ))}
              </AppOutlinedTextField>
            ) : null}

            {selectedPlanType === 'generic' && genericCalorieTarget && onGenericCalorieTargetChange ? (
              <Box sx={{ display: 'grid', gap: 1.2 }}>
                <AppOutlinedTextField
                  fullWidth
                  select
                  label="Calorie target method"
                  value={genericCalorieTarget.mode}
                  onChange={(event) => onGenericCalorieTargetChange({
                    ...genericCalorieTarget,
                    mode: event.target.value as GenericPlanCalorieTargetForm['mode'],
                  })}
                  size="small"
                  sx={[addMealPlanDialogOutlinedFieldSx, compactFieldSx]}
                >
                  <MenuItem value="direct">Enter daily calorie target</MenuItem>
                  <MenuItem value="reference_person">Calculate from a reference person</MenuItem>
                </AppOutlinedTextField>

                {genericCalorieTarget.mode === 'direct' ? (
                  <AppOutlinedTextField
                    fullWidth
                    label="Daily calorie target (kcal)"
                    type="number"
                    value={genericCalorieTarget.dailyCalories}
                    onChange={(event) => onGenericCalorieTargetChange({
                      ...genericCalorieTarget,
                      dailyCalories: event.target.value,
                    })}
                    error={Boolean(fieldErrors?.calorieTarget)}
                    helperText={fieldErrors?.calorieTarget || 'Meal-slot targets will be distributed to this daily total.'}
                    inputProps={{ min: 500, max: 6000, step: 50 }}
                    size="small"
                    sx={[addMealPlanDialogOutlinedFieldSx, compactFieldSx]}
                  />
                ) : (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.2 }}>
                    <AppOutlinedTextField
                      label="Age (years)"
                      type="number"
                      value={genericCalorieTarget.ageYears}
                      onChange={(event) => onGenericCalorieTargetChange({
                        ...genericCalorieTarget,
                        ageYears: event.target.value,
                      })}
                      error={Boolean(fieldErrors?.ageYears)}
                      helperText={fieldErrors?.ageYears}
                      inputProps={{ min: 1, max: 120, step: 1 }}
                      size="small"
                      sx={[addMealPlanDialogOutlinedFieldSx, compactFieldSx]}
                    />
                    <AppOutlinedTextField
                      label="Sex for EER formula"
                      select
                      value={genericCalorieTarget.gender}
                      onChange={(event) => onGenericCalorieTargetChange({
                        ...genericCalorieTarget,
                        gender: event.target.value as GenericPlanCalorieTargetForm['gender'],
                      })}
                      error={Boolean(fieldErrors?.gender)}
                      helperText={fieldErrors?.gender}
                      size="small"
                      sx={[addMealPlanDialogOutlinedFieldSx, compactFieldSx]}
                    >
                      <MenuItem value="female">Female</MenuItem>
                      <MenuItem value="male">Male</MenuItem>
                    </AppOutlinedTextField>
                    <AppOutlinedTextField
                      label="Weight (kg)"
                      type="number"
                      value={genericCalorieTarget.weightKg}
                      onChange={(event) => onGenericCalorieTargetChange({
                        ...genericCalorieTarget,
                        weightKg: event.target.value,
                      })}
                      error={Boolean(fieldErrors?.weightKg)}
                      helperText={fieldErrors?.weightKg}
                      inputProps={{ min: 1, max: 500, step: 0.1 }}
                      size="small"
                      sx={[addMealPlanDialogOutlinedFieldSx, compactFieldSx]}
                    />
                    <AppOutlinedTextField
                      label="Height (cm)"
                      type="number"
                      value={genericCalorieTarget.heightCm}
                      onChange={(event) => onGenericCalorieTargetChange({
                        ...genericCalorieTarget,
                        heightCm: event.target.value,
                      })}
                      error={Boolean(fieldErrors?.heightCm)}
                      helperText={fieldErrors?.heightCm}
                      inputProps={{ min: 30, max: 250, step: 0.1 }}
                      size="small"
                      sx={[addMealPlanDialogOutlinedFieldSx, compactFieldSx]}
                    />
                    <AppOutlinedTextField
                      label="Activity level"
                      select
                      value={genericCalorieTarget.activityLevel}
                      onChange={(event) => onGenericCalorieTargetChange({
                        ...genericCalorieTarget,
                        activityLevel: event.target.value,
                      })}
                      error={Boolean(fieldErrors?.activityLevel)}
                      helperText={fieldErrors?.activityLevel}
                      size="small"
                      sx={[addMealPlanDialogOutlinedFieldSx, compactFieldSx]}
                    >
                      <MenuItem value="sedentary">Sedentary</MenuItem>
                      <MenuItem value="lightly_active">Lightly active</MenuItem>
                      <MenuItem value="moderately_active">Moderately active</MenuItem>
                      <MenuItem value="very_active">Very active</MenuItem>
                      <MenuItem value="extremely_active">Extra active</MenuItem>
                    </AppOutlinedTextField>
                    <AppOutlinedTextField
                      label="Weight goal"
                      select
                      value={genericCalorieTarget.weightGoal}
                      onChange={(event) => onGenericCalorieTargetChange({
                        ...genericCalorieTarget,
                        weightGoal: event.target.value as GenericPlanCalorieTargetForm['weightGoal'],
                      })}
                      size="small"
                      sx={[addMealPlanDialogOutlinedFieldSx, compactFieldSx]}
                    >
                      <MenuItem value="moderate_loss">Moderate loss (−500 kcal)</MenuItem>
                      <MenuItem value="mild_loss">Mild loss (−250 kcal)</MenuItem>
                      <MenuItem value="maintain">Maintain</MenuItem>
                      <MenuItem value="mild_gain">Mild gain (+250 kcal)</MenuItem>
                      <MenuItem value="moderate_gain">Moderate gain (+500 kcal)</MenuItem>
                    </AppOutlinedTextField>
                  </Box>
                )}
              </Box>
            ) : null}
          </Stack>

          {formFeedback ? (
            <Box sx={{ mt: compactTypography ? 1.8 : 2 }}>
              <InlineFeedback
                variant={formFeedback.variant}
                title={formFeedback.title}
                message={formFeedback.message}
              />
            </Box>
          ) : null}

          {showPlanTypeOptions ? (
            <Box
              sx={{
                mt: 3,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: `repeat(${visibleAddMealPlanOptions.length}, minmax(0, 1fr))` },
                gap: 1.2,
              }}
            >
              {visibleAddMealPlanOptions.map((option) => {
                const selected = selectedPlanType === option.key;

                return (
                  <Box
                    key={option.key}
                    data-demo-id={`meal-plan-option-${option.key}`}
                    onClick={() => onSelectedPlanTypeChange(option.key)}
                    sx={{
                      position: 'relative',
                      borderRadius: '8px',
                      px: 1.6,
                      py: 1.45,
                      minHeight: 145,
                      background: option.gradient,
                      cursor: 'pointer',
                      border: selected ? '2px solid #ffffff' : '1px solid rgba(255,255,255,0.45)',
                      boxShadow: selected ? '0 0 0 2px rgba(37, 168, 218, 0.26)' : 'none',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        width: 18,
                        height: 18,
                        borderRadius: '4px',
                        border: '2px solid #fff',
                        bgcolor: selected ? '#fff' : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {selected && (
                        <CheckRoundedIcon
                          sx={{
                            fontSize: 16,
                            color:
                              option.key === 'manual'
                                ? '#3E8FD8'
                                : option.key === 'ai'
                                  ? '#2CB6C8'
                                  : option.key === 'generic'
                                    ? '#F0B449'
                                    : '#229FCB',
                            fontWeight: 'bold',
                          }}
                        />
                      )}
                    </Box>

                    <Typography
                      sx={{
                        mt: 7,
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '1.02rem',
                        lineHeight: 1.2,
                        fontWeight: 600,
                        color: '#fff',
                      }}
                    >
                      {option.title}
                    </Typography>

                    <Typography
                      sx={{
                        mt: 0.8,
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '0.78rem',
                        lineHeight: 1.25,
                        color: 'rgba(255,255,255,0.92)',
                      }}
                    >
                      {option.description}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          ) : null}

          <Box sx={{ mt: compactTypography ? 2.4 : 3, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={onSubmit}
              disabled={isSubmitting}
              data-analytics-ignore="true"
              sx={{
                minWidth: { xs: '100%', sm: compactTypography ? 220 : 260 },
                height: compactTypography ? 40 : 46,
                borderRadius: '8px',
                textTransform: 'none',
                fontFamily: 'Inter, sans-serif',
                fontSize: compactTypography ? '0.9rem' : '1.05rem',
                fontWeight: 600,
                bgcolor: '#25a8da',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#1498ca', boxShadow: 'none' },
                '&.Mui-disabled': {
                  bgcolor: '#b9dbe7',
                  color: '#ffffff',
                },
              }}
            >
              {isSubmitting ? submittingLabel : submitLabel}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
